import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { Container, Image, Form, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import "bootstrap/dist/css/bootstrap.min.css";
import "./signin-phone-otp";

import responsivebg from "../../assets/images/responsive-bg.png";
import backarrow from "../../assets/images/backarrow.jpg";
import logo from "../../assets/images/MTDlogo.png";

import * as authAction from "../../actions/auth";
import { useCookies } from "../../hooks/useCookies";
import { formatPhoneNumber } from "../../utils/utils";
import IconButton from "../userflow/components/card/IconButton";
import { handleUserNavigation } from "../../utils/handleUserRedirect";

// Constants
const OTP_TIMER_DURATION = 120; // 2 minutes in seconds
const OTP_LENGTH = 4;

// Custom hook for OTP timer management
const useOTPTimer = (duration) => {
  const [resendTimer, setResendTimer] = useState(duration);
  const [isResendDisabled, setIsResendDisabled] = useState(true);

  useEffect(() => {
    // Get the start time from localStorage, if it exists
    const startTime = localStorage.getItem("otpStartTime");
    const currentTime = new Date().getTime();

    if (startTime) {
      const elapsedTime = Math.floor((currentTime - startTime) / 1000);
      const remainingTime = duration - elapsedTime;
      setResendTimer(Math.max(remainingTime, 0));
      setIsResendDisabled(remainingTime > 0);
    } else {
      // Set start time if not already set
      localStorage.setItem("otpStartTime", currentTime);
    }

    // Timer function to update every second
    const timerInterval = setInterval(() => {
      setResendTimer(prev => {
        const newTimeLeft = prev - 1;
        if (newTimeLeft <= 0) {
          setIsResendDisabled(false);
          localStorage.removeItem("otpStartTime");
          return 0;
        }
        return newTimeLeft;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [duration]);

  const resetTimer = useCallback(() => {
    setResendTimer(duration);
    setIsResendDisabled(true);
    localStorage.setItem("otpStartTime", new Date().getTime());
  }, [duration]);

  return { resendTimer, isResendDisabled, resetTimer };
};

// Custom hook for OTP input management
const useOTPInput = (length, onCodeChange) => {
  const [codes, setCodes] = useState(Array(length).fill(""));

  // Create individual refs for each input
  const ref0 = useRef(null);
  const ref1 = useRef(null);
  const ref2 = useRef(null);
  const ref3 = useRef(null);

  // Create refs array from individual refs
  const refs = [ref0, ref1, ref2, ref3].slice(0, length);

  const handleCodeChange = useCallback((index, value) => {
    if (!isNaN(value) && value.length <= 1) {
      const newCodes = [...codes];
      newCodes[index] = value;
      setCodes(newCodes);

      // Clear error message when user starts typing
      if (onCodeChange) {
        onCodeChange();
      }

      // Auto-focus next input
      if (value.length === 1 && index < length - 1) {
        refs[index + 1].current?.focus();
      }
    }
  }, [codes, length, refs, onCodeChange]);

  const handleKeyDown = useCallback((index, e) => {
    if (e.key === "Backspace" && e.target.value === "" && index > 0) {
      refs[index - 1].current?.focus();
    }
  }, [refs]);

  const getOTPString = useCallback(() => codes.join(""), [codes]);

  const clearCodes = useCallback(() => {
    setCodes(Array(length).fill(""));
    refs[0].current?.focus();
  }, [length, refs]);

  return {
    codes,
    refs,
    handleCodeChange,
    handleKeyDown,
    getOTPString,
    clearCodes
  };
};

// OTP validation
const validateOTP = (otp) => {
  if (!otp || otp.length !== OTP_LENGTH) {
    return { isValid: false, message: "*Invalid verification code." };
  }

  if (!/^\d{4}$/.test(otp)) {
    return { isValid: false, message: "*Verification code must contain only numbers." };
  }

  return { isValid: true, message: "" };
};

// Format time utility
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
};

export const SignInPhoneOTP = () => {
  const navigate = useNavigate();
  const { getCookie, setCookie } = useCookies();

  const [loading, setLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const { resendTimer, isResendDisabled, resetTimer } = useOTPTimer(OTP_TIMER_DURATION);
  const { codes, refs, handleCodeChange, handleKeyDown, getOTPString, clearCodes } = useOTPInput(OTP_LENGTH, () => setErrorMessage(""));

  // Memoized OTP string
  const otpString = useMemo(() => getOTPString(), [getOTPString]);

  // Load phone number from cookie
  useEffect(() => {
    const savedPhoneNumber = getCookie("phoneNumber");
    if (savedPhoneNumber) {
      setPhoneNumber(savedPhoneNumber);
    }
  }, [getCookie]);

  // Handle form submission
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setErrorMessage("");

    const validation = validateOTP(otpString);
    if (!validation.isValid) {
      setErrorMessage(validation.message);
      return;
    }

    setLoading(true);

    try {
      const response = await authAction.loginPhone({
        phone: phoneNumber,
        otp: otpString
      });

      if (response && response.status === true) {
        // Set cookies
        setCookie("token", response.token, 30);
        setCookie("userId", response.data[0].user_id, 30);
        setCookie("Approval", response.data[0].approval, 30);
        setCookie("verified", response.data[0]?.email_verified_at ? true : false, 30);

        // Clear OTP timer
        localStorage.removeItem("otpStartTime");

        if (response.data && response.data.length > 0) {
          if (response.data[0].approval === 20 || response.data[0].approval === 15) {
            setCookie("Name", response.data[0].first_name, 30);
          }
          await handleUserNavigation(response.data, navigate, setLoading, '');
        }
      } else {
        setErrorMessage(`${response?.response?.data.message || response?.message || "Failed to verify code"}`);
        clearCodes(); // Clear OTP inputs on error
      }
    } catch (error) {
      console.error("Login error:", error);
      setErrorMessage("*An error occurred. Please try again later.");
      clearCodes(); // Clear OTP inputs on error
    } finally {
      setLoading(false);
    }
  }, [otpString, phoneNumber, setCookie, navigate, clearCodes]);

  // Handle resend OTP
  const handleResendOTP = useCallback(async (e) => {
    e.preventDefault();

    if (resendTimer === 0 && !loading) {
      setLoading(true);
      setErrorMessage("");

      try {
        const response = await authAction.loginPhoneOTP({ phone: phoneNumber });

        if (response && response.success) {
          resetTimer();
          clearCodes();
        } else {
          const errorMsg = response?.message || "Failed to resend code";
          setErrorMessage(`*${errorMsg}`);
        }
      } catch (error) {
        console.error("Resend OTP error:", error);
        setErrorMessage("*An error occurred. Please try again later.");
      } finally {
        setLoading(false);
      }
    }
  }, [resendTimer, loading, phoneNumber, resetTimer, clearCodes]);

  // Handle back navigation
  const handleBackNavigation = useCallback(() => {
    localStorage.removeItem("otpStartTime");
    window.history.back();
  }, []);

  return (
    <div className="entercode-container" role="main">
      <div className="entercode-bg">
        <Image
          className="responsive-bg"
          src={responsivebg}
          alt="Background"
          loading="lazy"
        />
      </div>

      <Container className="entercode-main">
        <Container className="entercode-box">
          <Container className="logo-progressbar2">
            <Container className="logo-arrow2">
              <Image
                src={backarrow}
                className="backarrow"
                onClick={handleBackNavigation}
                alt="Go back"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleBackNavigation();
                  }
                }}
                style={{ cursor: 'pointer' }}
              />
              <Image
                src={logo}
                alt="Logo"
                className="logo"
                style={{ backgroundColor: "transparent" }}
                loading="lazy"
              />
            </Container>
          </Container>

          <Container className="entercode-text">
            <IconButton
              type="phone"
              onClick={(e) => e.stopPropagation()}
              aria-label="Phone icon"
            />
          </Container>

          <Form onSubmit={handleSubmit} className="entercode-form">
            <Container className="entercode-content">
              <div>
                <Form.Group
                  className="entercode-form-group"
                >
                  <Form.Label className="entercode-lable" htmlFor="otp-input-0">
                    Enter Verification Code
                  </Form.Label>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                    role="group"
                    aria-label="OTP verification code inputs"
                  >
                    {codes.map((code, index) => (
                      <Form.Control
                        key={index}
                        id={`otp-input-${index}`}
                        className={`entercode-input ${errorMessage ? "error" : ""}`}
                        type="tel"
                        ref={refs[index]}
                        placeholder=""
                        value={code}
                        onChange={(e) => handleCodeChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        style={{
                          flex: 1,
                          marginLeft: index > 0 ? "10px" : "0"
                        }}
                        maxLength={1}
                        autoComplete="off"
                        onCopy={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onCut={(e) => e.preventDefault()}
                        disabled={loading}
                        aria-describedby={errorMessage ? "otpError" : undefined}
                        aria-invalid={!!errorMessage}
                      />
                    ))}
                  </div>
                  {phoneNumber && (
                    <p style={{ marginTop: "5px", marginBottom: "5px", color: "#666", fontSize: "14px" }}>
                      Code sent to {formatPhoneNumber(phoneNumber)}
                    </p>
                  )}
                  <div style={{ marginTop: "5px" }}>
                    {errorMessage && (
                      <Form.Text
                        id="otpError"
                        className="text-danger error-message"
                        role="alert"
                      >
                        {errorMessage}
                      </Form.Text>
                    )}
                  </div>
                </Form.Group>

                <div className="resend-timer">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={isResendDisabled || loading}
                    style={{
                      color: isResendDisabled ? "#cbcbcb" : "#5E5E5E",
                      background: "none",
                      border: "none",
                      textDecoration: "underline",
                      cursor: isResendDisabled ? "not-allowed" : "pointer"
                    }}
                    aria-label={isResendDisabled ? "Resend code disabled" : "Resend verification code"}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                    ) : (
                      "Resend code"
                    )}
                  </button>
                  <span aria-live="polite">
                    {formatTime(resendTimer)} sec
                  </span>
                </div>
              </div>
              <button
                type="submit"
                className="global-next-btn"
                disabled={loading}
                aria-describedby={loading ? "submitStatus" : undefined}
              >
                {loading ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  "Next"
                )}
              </button>
            </Container>
          </Form>
        </Container>
      </Container>
    </div>
  );
};
